import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { TokenProvider } from './contexts/TokenContext';
import LoadingSpinner from './components/LoadingSpinner';
import './App.css';

// Eagerly load critical pages for fast initial render
import LoginPage from './pages/LoginPage';
import GrowPage from './pages/GrowPage';

// Lazy load all other pages for code splitting
const ModulesPage = React.lazy(() => import('./pages/ModulesPage'));

// Preload all pages after initial render for smooth navigation
const preloadPages = () => {
  // Delay preload to not block initial render
  setTimeout(() => {
    // User pages
    import('./pages/ModulesPage');
    import('./pages/HowToLibraryPage');
    import('./pages/RewardsPage');
    import('./pages/MyAnalyticsPage');
    import('./pages/ProfilePage');
    import('./pages/TeamsPage');
    // Admin pages
    import('./pages/AdminAnalyticsPage');
    import('./pages/AdminActivityPage');
    import('./pages/AdminRewardsPage');
    import('./pages/ManageModulesPage');
    import('./pages/ApprovalQueuePage');
    import('./pages/UsersPage');
    import('./pages/TeamManagementPage');
    // Other pages
    import('./pages/DiscoveryPage');
  }, 1000);
};
if (typeof window !== 'undefined') {
  preloadPages();
}
const ModuleDetailPage = React.lazy(() => import('./pages/ModuleDetailPage'));
const TeamsPage = React.lazy(() => import('./pages/TeamsPage'));
const CreateModulePage = React.lazy(() => import('./pages/CreateModulePage'));
const EditModulePage = React.lazy(() => import('./pages/EditModulePage'));
const ManageModulesPage = React.lazy(() => import('./pages/ManageModulesPage'));
const HowToLibraryPage = React.lazy(() => import('./pages/HowToLibraryPage'));
const CreateHowToPage = React.lazy(() => import('./pages/CreateHowToPage'));
const HowToDetailPage = React.lazy(() => import('./pages/HowToDetailPage'));
const EditHowToPage = React.lazy(() => import('./pages/EditHowToPage'));
const UsersPage = React.lazy(() => import('./pages/UsersPage'));
const ApprovalQueuePage = React.lazy(() => import('./pages/ApprovalQueuePage'));
const TeamManagementPage = React.lazy(() => import('./pages/TeamManagementPage'));
const RewardsPage = React.lazy(() => import('./pages/RewardsPage'));
const AdminRewardsPage = React.lazy(() => import('./pages/AdminRewardsPage'));
const AdminAnalyticsPage = React.lazy(() => import('./pages/AdminAnalyticsPage'));
const AdminActivityPage = React.lazy(() => import('./pages/AdminActivityPage'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));
const MyAnalyticsPage = React.lazy(() => import('./pages/MyAnalyticsPage'));
const DiscoveryPage = React.lazy(() => import('./pages/DiscoveryPage'));

// Protected route component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner fullScreen />;
  return user ? <>{children}</> : <Navigate to="/login" replace />;
};

// Admin protected route
const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isAdmin, loading } = useAuth();
  if (loading) return <LoadingSpinner fullScreen />;
  return user && isAdmin ? <>{children}</> : <Navigate to="/grow" replace />;
};

function AppRoutes() {
  const { user, loading } = useAuth();

  // If still loading auth state, show loading indicator
  if (loading) return <LoadingSpinner fullScreen />;

  // Minimal fallback to reduce flash during lazy load
  const PageFallback = () => (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950" />
  );

  return (
    <Suspense fallback={<PageFallback />}>
    <Routes>
      <Route path="/login" element={
        user ? <Navigate to="/grow" replace /> : <LoginPage />
      } />
      <Route path="/" element={<Navigate to="/grow" replace />} />
      <Route path="/grow" element={
        <ProtectedRoute>
          <GrowPage />
        </ProtectedRoute>
      } />
      <Route path="/modules" element={
        <ProtectedRoute>
          <ModulesPage />
        </ProtectedRoute>
      } />
      <Route path="/modules/:id" element={
        <ProtectedRoute>
          <ModuleDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/teams" element={
        <ProtectedRoute>
          <TeamsPage />
        </ProtectedRoute>
      } />
      <Route path="/create" element={
        <ProtectedRoute>
          <CreateModulePage />
        </ProtectedRoute>
      } />
      <Route path="/edit/:id" element={
        <AdminRoute>
          <EditModulePage />
        </AdminRoute>
      } />
      <Route path="/manage" element={
        <AdminRoute>
          <ManageModulesPage />
        </AdminRoute>
      } />
      <Route path="/how-to" element={
        <ProtectedRoute>
          <HowToLibraryPage />
        </ProtectedRoute>
      } />
      <Route path="/how-to/new" element={
        <ProtectedRoute>
          <CreateHowToPage />
        </ProtectedRoute>
      } />
      <Route path="/how-to/:id" element={
        <ProtectedRoute>
          <HowToDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/how-to/edit/:id" element={
        <AdminRoute>
          <EditHowToPage />
        </AdminRoute>
      } />
      <Route path="/users" element={
        <AdminRoute>
          <UsersPage />
        </AdminRoute>
      } />
      <Route path="/approvals" element={
        <AdminRoute>
          <ApprovalQueuePage />
        </AdminRoute>
      } />
      <Route path="/team-management" element={
        <AdminRoute>
          <TeamManagementPage />
        </AdminRoute>
      } />
      <Route path="/admin/rewards" element={
        <AdminRoute>
          <AdminRewardsPage />
        </AdminRoute>
      } />
      <Route path="/admin/analytics" element={
        <AdminRoute>
          <AdminAnalyticsPage />
        </AdminRoute>
      } />
      <Route path="/admin/activity" element={
        <AdminRoute>
          <AdminActivityPage />
        </AdminRoute>
      } />
      <Route path="/admin/discovery" element={
        <AdminRoute>
          <DiscoveryPage />
        </AdminRoute>
      } />
      <Route path="/rewards" element={
        <ProtectedRoute>
          <RewardsPage />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/my-analytics" element={
        <ProtectedRoute>
          <MyAnalyticsPage />
        </ProtectedRoute>
      } />
      <Route path="*" element={<Navigate to="/grow" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ThemeProvider>
      <Router>
        <AuthProvider>
          <TokenProvider>
            <AppRoutes />
          </TokenProvider>
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App; 